import { useEffect, useRef, useState } from 'react';

import CloseIcon from '@/app/assets/svg/close.svg?react';
import {
  getVaultContracts,
  vaultUsesWyldsAsset,
} from '@/app/shared/constants/web3/chainContracts';
import { WYLDS } from '@/app/shared/constants/web3/constants';
import { resolveVaultChainId } from '@/app/shared/hooks/blockchain/useChainContracts';
import { useConstrainedScrollArea } from '@/app/shared/hooks/useConstrainedScrollArea';
import { CustomScrollbar } from '@/app/shared/ui/CustomScrollbar';
import { Modal } from '@/app/shared/ui/Modal';
import { Tabs } from '@/app/shared/ui/Tabs';

type VaultInfoTab = 'overview' | 'contracts' | 'pricing' | 'yield';

interface InfoRow {
  label: string;
  value: string;
  href?: string;
}

interface VaultInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  vaultId: string | null;
  vaultName?: string | null;
}

const TABS = [
  { id: 'overview' as const, content: 'Overview' },
  { id: 'contracts' as const, content: 'Contracts' },
  { id: 'pricing' as const, content: 'Pricing & NAV' },
  { id: 'yield' as const, content: 'Yield mechanics' },
];

const META_LINE = 'Active · Mainnet deployment · 12 Oct 2026';

const EVM_STRATEGY_ADDRESSES = [
  '0x8D6f5f37350672c329157A8B3Eee96D6148a7A24',
  '0x43bC5fB52A618F0F2efb0d68Cf900Bb2FbD8a501',
  '0xBB03549117e38103589774AE1D6338D1B237FFA2',
  '0x9A1dE53C14215272ae4dCFE35a650fd7151dc403',
  '0x97f2076f2373A9a28CF63CC419e6cc00eDAD75C0',
  '0xC9DDDA6591c8f639716dDc15c55589695Eac326C',
  '0xdF6C675700a22fC83e01EEcfdAaAC7E9bB49FF29',
];

const SVM_STRATEGY_ADDRESSES = ['5eP1ZevYtTNxQs5yzRqvpDgeZmZdcsJEscVLQAFYVMsV'];

const NAV_FEEDS_URL =
  'https://app.redstone.finance/push-feeds?search=mzprime&testnets=true';

const OVERVIEW_ROWS: InfoRow[] = [
  { label: 'Deposit asset', value: 'wYLDS' },
  { label: 'Epoch length', value: '1 week (604,800 seconds)' },
  { label: 'Deposit chain', value: 'Ethereum mainnet' },
  { label: 'Strategy-position chains', value: 'Ethereum and Solana' },
  { label: 'Strategy', value: 'Hastra PRIME looped through Morpho and Kamino' },
  {
    label: 'Underlying assets',
    value: 'PRIME, AUTO, SMB, wYLDS, USDC, PYUSD, AUSD, RLUSD, OUSD, CASH, USDG',
  },
  { label: 'External protocols', value: 'Morpho, Aave, Kamino, Exponent' },
  { label: 'Enabled tranches', value: 'msHastra (Senior) and mjHastra (Junior)' },
];

const CONTRACTS_ROWS_FALLBACK: InfoRow[] = [
  { label: 'Vault contract', value: 'TBD from production deployment' },
  { label: 'Deposit asset', value: `wYLDS · ${WYLDS}` },
  { label: 'Base token', value: 'mzUSD · non-yield-bearing accounting and conversion token' },
  { label: 'Base-token contract', value: 'TBD from production deployment' },
  { label: 'Senior token', value: 'msHastra · contract TBD' },
  { label: 'Junior token', value: 'mjHastra · contract TBD' },
  { label: 'Deposit route', value: 'wYLDS → mzUSD → selected tranche token' },
  { label: 'Redemption route', value: 'Tranche token → mzUSD → wYLDS' },
  { label: 'EVM Strategy Addresses', value: EVM_STRATEGY_ADDRESSES.join('\n') },
  { label: 'SVM Strategy Addresses', value: SVM_STRATEGY_ADDRESSES.join('\n') },
];

const getContractsRows = (vaultId: string | null): InfoRow[] => {
  const chainId = vaultId ? resolveVaultChainId(vaultId) : undefined;
  const contracts = vaultId && chainId ? getVaultContracts(chainId, vaultId) : undefined;
  if (!contracts?.VAULT) return CONTRACTS_ROWS_FALLBACK;

  const isWylds = vaultUsesWyldsAsset(contracts);
  const seniorSymbol = isWylds ? 'msHastra' : 'msPRIME';
  const juniorSymbol = isWylds ? 'mjHastra' : 'mjPRIME';
  const depositAsset = isWylds ? `wYLDS · ${contracts.ASSET}` : `USDC · ${contracts.USDC}`;
  const depositRoute = isWylds
    ? 'wYLDS → mzUSD → selected tranche token'
    : 'USDC → mzUSD → selected tranche token';
  const redemptionRoute = isWylds
    ? 'Tranche token → mzUSD → wYLDS'
    : 'Tranche token → mzUSD → USDC';

  return [
    { label: 'Vault contract', value: contracts.VAULT },
    { label: 'Deposit asset', value: depositAsset },
    { label: 'Base token', value: 'mzUSD · non-yield-bearing accounting and conversion token' },
    { label: 'Base-token contract', value: contracts.MZUSD },
    { label: 'Senior token', value: `${seniorSymbol} · ${contracts.SENIOR_TRANCHE}` },
    { label: 'Junior token', value: `${juniorSymbol} · ${contracts.JUNIOR_TRANCHE}` },
    { label: 'Deposit route', value: depositRoute },
    { label: 'Redemption route', value: redemptionRoute },
    { label: 'EVM Strategy Addresses', value: EVM_STRATEGY_ADDRESSES.join('\n') },
    { label: 'SVM Strategy Addresses', value: SVM_STRATEGY_ADDRESSES.join('\n') },
  ];
};

const PRICING_ROWS: InfoRow[] = [
  { label: 'Hastra PRIME', value: 'Valued according to NAV' },
  { label: 'Hastra AUTO', value: 'Valued according to NAV' },
  { label: 'Hastra SMB', value: 'Valued according to NAV' },
  { label: 'wYLDS', value: 'Valued according to its USD reference price' },
  { label: 'Base token (mzUSD)', value: 'Vault-defined wYLDS conversion (1:1 scaled)' },
  { label: 'Base-token contract', value: 'Valued 1:1 against wYLDS (scaled decimals)' },
  { label: 'Senior token', value: 'msHastra · shown in the Strategy performance chart when verified' },
  { label: 'Junior token', value: 'mjHastra · shown in the Strategy performance chart when verified' },
  { label: 'NAV feeds', value: NAV_FEEDS_URL, href: NAV_FEEDS_URL },
];

const YIELD_ROWS: InfoRow[] = [
  {
    label: 'Gross strategy yield',
    value: 'Underlying strategy performance before applicable fees and costs',
  },
  {
    label: 'Net distributable yield',
    value: 'Positive yield remaining after applicable fees and costs',
  },
  {
    label: 'Base Reward',
    value: 'Distributed proportionally across both yield-bearing tranches (80% of net)',
  },
  {
    label: 'Risk Premium',
    value: 'Allocated to mjHastra for its subordinate loss position (20% risk premium)',
  },
  { label: 'Target ratio', value: '80% Senior / 20% Junior' },
  { label: 'Loss order', value: 'Junior → Senior' },
  { label: 'Performance fee', value: '10% (1,000 bps)' },
  { label: 'Protocol fee', value: '2.5% (250 bps)' },
  {
    label: 'Unstake delay',
    value: 'Senior 1 epoch · Junior 2 epochs',
  },
  {
    label: 'Yield allocation',
    value:
      'Base Reward is shared across yield-bearing tranches; Risk Premium compensates subordinate capital',
  },
];

const OVERVIEW_RISKS =
  'Borrowing costs, negative carry, liquidation, asset depeg, liquidity and redemption constraints, oracle and NAV errors, external protocol failure, execution and automation failure, smart-contract risk and withdrawal delay.';

const TAB_ROWS: Record<Exclude<VaultInfoTab, 'contracts'>, InfoRow[]> = {
  overview: OVERVIEW_ROWS,
  pricing: PRICING_ROWS,
  yield: YIELD_ROWS,
};

const InfoRows = ({ rows }: { rows: InfoRow[] }) => (
  <div className='flex w-full flex-col'>
    {rows.map((row) => (
      <div
        key={row.label}
        className='border-decoration-stroke flex w-full flex-col gap-3 border-b py-4 md:flex-row md:gap-0 md:py-0'
      >
        <span className='border-decoration-stroke w-full shrink-0 font-geist text-base leading-6 font-normal text-(--vault-info-label) md:w-75 md:border-r md:py-4'>
          {row.label}
        </span>
        {row.href ? (
          <a
            href={row.href}
            target='_blank'
            rel='noopener noreferrer'
            className='min-w-0 flex-1 font-geist text-base leading-6 font-normal break-all text-brand-1100 underline md:px-5 md:py-4'
          >
            {row.value}
          </a>
        ) : (
          <span className='min-w-0 flex-1 whitespace-pre-line font-geist text-base leading-6 font-normal wrap-break-word text-(--vault-info-value) md:px-5 md:py-4'>
            {row.value}
          </span>
        )}
      </div>
    ))}
  </div>
);

const VaultInfoModal = ({ isOpen, onClose, vaultId, vaultName }: VaultInfoModalProps) => {
  const [activeTab, setActiveTab] = useState<VaultInfoTab>('overview');
  const title = vaultName?.trim() || 'Vault';
  const headerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const contractsRows = getContractsRows(vaultId);
  const rows = activeTab === 'contracts' ? contractsRows : TAB_ROWS[activeTab];

  const { maxHeight, canScroll, isReady } = useConstrainedScrollArea({
    enabled: isOpen,
    contentKey: activeTab,
    headerRef,
    scrollRef,
  });

  useEffect(() => {
    if (isOpen) setActiveTab('overview');
  }, [isOpen, vaultId]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className={`relative flex w-full max-w-250 flex-col overflow-hidden rounded-xl border-0 bg-(--vault-info-bg) p-5 max-md:h-[calc(100dvh-80px)] md:max-h-[min(698px,90vh)] lg:p-10 ${isReady ? '' : 'invisible'}`}
    >
      <div ref={headerRef} className='flex w-full shrink-0 flex-col gap-5.25'>
        <div className='flex h-7 w-full items-start justify-between'>
          <span className='w-fit rounded-md bg-(--vault-info-chip-bg) px-1.75 py-px font-geist text-sm leading-5 font-normal text-(--vault-info-label)'>
            Vault information
          </span>
          <button
            type='button'
            onClick={onClose}
            className='flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-(--vault-info-chip-bg) text-modal-close-icon transition-colors'
            aria-label='Close'
          >
            <CloseIcon className='h-3.5 w-3.5' />
          </button>
        </div>
        <h2 className='text-[30px] leading-9 font-normal text-brand-1100'>{title}</h2>
        <p className='text-sm leading-5 text-gray-400'>{META_LINE}</p>
        <div className='border-decoration-stroke w-full border-b' />
        <div className='border-decoration-stroke w-full border-b'>
          <Tabs
            tabs={TABS}
            activeTab={activeTab}
            onTabChange={(id) => setActiveTab(id as VaultInfoTab)}
            className='flex w-full items-end gap-5 overflow-x-auto md:overflow-hidden [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
            tabClassName='-mb-px shrink-0 whitespace-nowrap border-b-2 border-transparent pb-4 text-center font-geist text-base leading-6 font-medium transition-colors'
            activeTabClassName='border-(--vault-info-tab-active) text-(--vault-info-tab-active)'
            inactiveTabClassName='text-(--vault-info-tab-inactive) hover:opacity-80'
          />
        </div>
      </div>

      <CustomScrollbar ref={scrollRef} canScroll={canScroll} maxHeight={maxHeight} resetKey={activeTab}>
        <InfoRows rows={rows} />

        {activeTab === 'overview' && (
          <div className='mt-6 flex flex-col gap-2.5 rounded-md bg-(--vault-info-banner-bg) p-6'>
            <p className='text-sm font-medium text-(--vault-info-banner-title)'>Strategy-specific risks</p>
            <p className='text-sm leading-6 text-(--vault-info-banner-text)'>{OVERVIEW_RISKS}</p>
          </div>
        )}
      </CustomScrollbar>
    </Modal>
  );
};

export default VaultInfoModal;
